import React, {
	type AnchorHTMLAttributes,
	type ButtonHTMLAttributes,
} from 'react';

import Badge, { type Props as BadgeProps } from 'components/Badge';
import type { IconDirection, IconName } from 'components/Icon';
import Icon from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import { cn, cnm } from 'utils/classNames';

export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'cta' | 'branded' | 'secondary' | 'borderless';

interface BaseProps {
	/** Small floating badge */
	badge?: string | number;

	/** Extra props for the small floating badge */
	badgeProps?: Partial<BadgeProps>;

	/** Additional class names */
	className?: string;

	/** If the button is disabled, or without href for links */
	disabled?: boolean;

	/** Icon name */
	icon: IconName;

	/** Icon direction/rotation */
	iconDirection?: IconDirection;

	/** Disable and show a loading spinner */
	isLoading?: boolean;

	/** Button size */
	size?: IconButtonSize;

	/** Text label */
	text: string;

	/** Classes for the text label, when visible */
	textClassName?: string;

	/** How the text should be displayed */
	textDisplay?: 'leading' | 'trailing' | 'hidden';

	/** Button style variant */
	variant?: IconButtonVariant;
}

type AnchorAttrs = AnchorHTMLAttributes<HTMLAnchorElement>;
type ButtonAttrs = ButtonHTMLAttributes<HTMLButtonElement>;

interface AnchorProps extends BaseProps, Omit<AnchorAttrs, 'children'> {
	type?: never;

	/** Link URL, results in an anchor instead of a button */
	href: string;
}

interface ButtonProps extends BaseProps, Omit<ButtonAttrs, 'children'> {
	href?: never;

	/** Button click handler */
	onClick: ButtonAttrs['onClick'];
}

type Props = AnchorProps | ButtonProps;

/** Icon button with a gray circle on hover. */
const IconButton = React.forwardRef<
	HTMLAnchorElement | HTMLButtonElement,
	Props
>(
	(
		{
			badge,
			badgeProps,
			className,
			disabled = false,
			href,
			icon,
			iconDirection,
			isLoading = false,
			onClick,
			size = 'md',
			text,
			textClassName = '',
			textDisplay = 'hidden',
			type = 'button',
			variant = 'borderless',
			...attrs
		},
		ref,
	) => {
		const isInteractive = !disabled && !isLoading;
		const isTextVisible = textDisplay !== 'hidden';
		const textNode = isTextVisible ? (
			<span
				aria-hidden="true"
				className={cn(
					textDisplay === 'leading' && 'mr-2',
					textDisplay === 'trailing' && 'ml-2',
					disabled && 'text-disabled',
					isInteractive && 'group-hover/icon-button:underline',
					textClassName,
				)}
			>
				{text}
			</span>
		) : null;

		return (
			<LinkOrButton
				{...attrs}
				ref={ref}
				aria-label={badge ? `${text} (${badge})` : text}
				className={cnm(
					'group/icon-button inline-flex items-center rounded-full',
					// Make the hit area larger for small sizes.
					(size === 'xs' || size === 'sm') &&
						isInteractive && [
							'relative after:absolute',
							// Don't expand horizontally if the text is visible.
							size === 'xs' &&
								(isTextVisible
									? 'after:-inset-y-3 after:inset-x-0'
									: 'after:-inset-3'),
							size === 'sm' &&
								(isTextVisible
									? 'after:-inset-y-1.5 after:inset-x-0'
									: 'after:-inset-1.5'),
						],
					className,
				)}
				disabled={disabled || isLoading}
				href={href}
				onClick={onClick}
				type={type}
			>
				{textDisplay === 'leading' && textNode}
				<span
					aria-hidden="true"
					className={cnm(
						'relative inline-flex items-center justify-center rounded-full',

						// Always have a border for forced colors mode.
						'border border-transparent',
						isInteractive && 'forced-colors:group-hover/icon-button:outline',

						size === 'xs' && 'size-6',
						size === 'sm' && 'size-9',
						size === 'md' && 'size-12',
						size === 'lg' && 'size-14',

						variant === 'branded' && [
							!disabled && 'bg-brand text-inverse',
							isInteractive &&
								'group-hover/icon-button:bg-brandHover group-active/icon-button:bg-brandActive',
							disabled && 'bg-disabled text-disabled',
						],
						variant === 'cta' && [
							!disabled && 'bg-action text-inverse',
							isInteractive &&
								'group-hover/icon-button:bg-actionHover group-active/icon-button:bg-actionActive',
							disabled && 'bg-disabled text-disabled',
						],
						variant === 'secondary' && [
							'bg-base',
							!disabled && 'border-strong text-base',
							isInteractive &&
								'group-hover/icon-button:bg-baseHover group-active/icon-button:bg-baseActive',
							disabled && 'border-subtle text-disabled',
						],
						variant === 'borderless' && [
							'bg-transparent',
							!disabled && 'text-base',
							isInteractive &&
								'group-hover/icon-button:bg-inverse/20 group-active/icon-button:bg-inverse/30',
							disabled && 'text-disabled',
						],
					)}
				>
					<Icon
						align="top"
						direction={iconDirection}
						size={size === 'xs' || size === 'sm' ? 20 : 24}
						name={isLoading ? 'spinner' : icon}
						className={isLoading ? 'animate-spinFast' : undefined}
					/>
					<Badge
						{...badgeProps}
						text={badge}
						size={size === 'xs' || size === 'sm' ? 'sm' : 'md'}
						className={cn(
							'absolute',
							size === 'xs' && '-right-1.5 -top-1.5',
							size === 'sm' && '-right-0.5 -top-0.5',
							size === 'md' && 'right-0 top-0',
							size === 'lg' && 'right-0.5 top-0.5',
						)}
					/>
				</span>
				{textDisplay === 'trailing' && textNode}
			</LinkOrButton>
		);
	},
);
IconButton.displayName = 'IconButton';

export default IconButton;
